/**
 * Tokens de layout (classes Tailwind) da Home Editorial "Business Insider 3 colunas".
 * Source of truth ÚNICO das classes — os componentes nunca duplicam strings.
 * Portado fielmente de shadcn-ui-blocks-master/src/components/block/home-bi-layout.ts.
 */
import { SITE_CONTAINER_CLASS } from '@/constants/siteLayout'

/** Grelha 3 colunas: coluna larga | coluna média (350px) | sidebar (340px). */
export const HOME_BI_THREE_COL_GRID =
  'grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,350px)_340px] lg:gap-x-8 lg:gap-y-6 xl:gap-x-10'

/**
 * Container central da home — reexporta a FONTE DE VERDADE única do site (`SITE_CONTAINER_CLASS`),
 * alinhada ao header (`SiteHeader`/`HeaderCategoryRail`). Mantido como alias por compatibilidade
 * com os componentes da Home Editorial; valor idêntico ao anterior (zero-regressão).
 */
export const HOME_BI_CONTAINER = SITE_CONTAINER_CLASS

/** Shell: bloco de notícias + bloco de sidebar (containers separados). */
export const HOME_BI_NEWS_SIDEBAR_SHELL =
  'flex w-full flex-col gap-6 lg:flex-row lg:items-start lg:gap-0'

export const HOME_BI_NEWS_CONTAINER = 'min-w-0 w-full flex-1'

export const HOME_BI_SIDEBAR_CONTAINER =
  'hidden min-w-0 shrink-0 lg:block lg:w-[340px] lg:max-w-[340px] lg:pl-8'

/** Borda esquerda da sidebar (middle, footer — não no hero). */
export const HOME_BI_SIDEBAR_BORDER = 'lg:border-border lg:border-l'

export const HOME_BI_SIDEBAR_INNER = 'w-full lg:sticky lg:top-4'

export const HOME_BI_SIDEBAR_MOBILE = 'mt-6 min-w-0 w-full lg:hidden'

/** Grelha só notícias (2 colunas) no footer e referência hero. */
export const HOME_BI_NEWS_TWO_COL_GRID =
  'grid w-full grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,350px)] lg:gap-x-8 lg:gap-y-6 xl:gap-x-10'

/** Grelha `home-middle` (só notícias): coluna A ~31rem + coluna B `1fr`. */
export const HOME_MIDDLE_NEWS_GRID =
  'grid w-full grid-cols-1 gap-5 lg:grid-cols-[minmax(0,31rem)_minmax(0,1fr)] lg:grid-rows-[auto_auto] lg:gap-x-0 lg:gap-y-0'

/** Padding horizontal entre colunas e a linha divisória (desktop, home-middle). */
export const HOME_MIDDLE_COL_GUTTER = 'lg:px-8' as const

/** Padding vertical superior das faixas footer (sem pb — ritmo vem da faixa seguinte). */
export const HOME_BI_BAND_PY = 'pt-6 md:pt-8'

/** Padding vertical simétrico da faixa `home-middle`. */
export const HOME_MIDDLE_BAND_PY = 'py-6 md:py-8'

/** Padding inferior da faixa hero (antes do separador da próxima seção). */
export const HOME_BI_BAND_PB = 'pb-6 md:pb-8'

/** Título de seção (middle, videos, footer) — ~20px mobile; ~24px desktop. */
export const HOME_BI_SECTION_HEADING =
  'text-xl leading-snug font-semibold tracking-tight lg:text-2xl lg:leading-tight'

/** Bloco de notícia — hover em qualquer parte ativa o zoom na imagem. */
export const HOME_BI_NEWS_ITEM_GROUP = 'group'

/** Zoom suave na imagem (igual destaque principal do hero). */
export const HOME_BI_NEWS_IMAGE_HOVER =
  'rounded-none object-cover transition-transform duration-500 group-hover:scale-[1.02]'

/** Título da notícia — sublinhado no hover do link ou do bloco (`group`). */
export const HOME_BI_NEWS_TITLE_LINK =
  'underline decoration-transparent underline-offset-[0.2em] decoration-2 transition-[text-decoration-color,color,opacity] duration-200 hover:underline hover:decoration-foreground/80 hover:opacity-90 group-hover:underline group-hover:decoration-foreground/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

/** Título sobre imagem escura (carrossel mobile do hero). */
export const HOME_BI_NEWS_TITLE_LINK_ON_DARK =
  'underline decoration-transparent underline-offset-[0.2em] decoration-2 transition-[text-decoration-color,opacity] duration-200 group-hover:underline group-hover:decoration-white/90'

/** Thumb da sidebar "Últimas": 100×75 fixo; radius global (aba Imagens, 0 = quadrado). */
export const HOME_LATEST_SIDEBAR_THUMB =
  'relative h-[75px] w-[100px] shrink-0 overflow-hidden rounded-[var(--post-thumb-radius,0px)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'

/** Barra vermelha BI "live" sob o título do widget (#ea0201, 32×4px, 2px abaixo do texto). */
export const TOUT_LAYOUT_HEADING_BAR =
  "is-live relative pb-1.5 after:absolute after:left-0 after:top-[calc(100%+2px)] after:block after:h-1 after:w-8 after:bg-[#ea0201] after:content-['']"

/** Separador entre faixas (borda superior + respiro interno via HOME_BI_BAND_PY). */
export const HOME_BI_BAND_DIVIDER = 'border-border border-t'

/** Faixa `home-videos` — largura total (fora do container da página), fundo muted. */
export const HOME_BI_VIDEOS_BAND = 'w-full border-border bg-muted/40 border-y'

/** Respiro vertical extra na faixa de vídeos (topo e base). */
export const HOME_BI_VIDEOS_BAND_PY = 'py-10 md:py-12 lg:py-14'

/** Vídeos: alinhado ao container, sem vazar à esquerda. */
export const HOME_BI_CAROUSEL_VIEWPORT = 'w-full min-w-0 overflow-x-clip'

/**
 * Posts no X: shell + container de sombras; peek à direita;
 * `clip-path` só corta à esquerda (não esconde cards à direita).
 */
export const HOME_BI_X_CAROUSEL_SHELL =
  '@container/carousel w-full min-w-0 [clip-path:inset(0_-100vw_0_0)] [--home-x-peek:5rem] md:[--home-x-peek:6rem]'

/** Container do trilho + sombras (HomeXCarouselEdgeFades). */
export const HOME_BI_X_CAROUSEL_CONTAINER = 'relative isolate w-full min-w-0'

/** Trilho = largura do container (4 cards inteiros; o 5º entra ao rolar). */
export const HOME_BI_X_CAROUSEL_TRACK = 'relative w-full min-w-0'

/** Texto traduzido nos cards de posts do X — altura e fonte uniformes. */
export const HOME_X_POST_TRANSLATED_TEXT =
  'text-foreground min-h-[8.25rem] text-sm leading-relaxed line-clamp-6'

/** IDs estáveis das faixas modulares da home BI. */
export const HOME_BI_SECTION = {
  hero: 'home-hero',
  middle: 'home-middle',
  videos: 'home-videos',
  footer: 'home-footer',
} as const

export type HomeBiSectionId = (typeof HOME_BI_SECTION)[keyof typeof HOME_BI_SECTION]

export function homeBiSectionSidebarId(sectionId: HomeBiSectionId) {
  return `${sectionId}-sidebar`
}

export function homeBiDesktopOnly(breakpoint: 'lg' | 'md' = 'lg') {
  return breakpoint === 'lg' ? 'hidden lg:block' : 'hidden md:block'
}

/** Grelha hero 2 colunas — activação alinhada ao `breakpoint` do `BusinessInsiderDestaque`. */
export function homeBiHeroGridClass(breakpoint: 'md' | 'lg' = 'lg') {
  const base = 'grid w-full grid-cols-1 gap-6 md:gap-y-6'
  if (breakpoint === 'lg') {
    return `${base} lg:grid-cols-[minmax(0,1fr)_minmax(0,350px)] lg:gap-x-8 xl:gap-x-10`
  }
  return `${base} md:grid-cols-[minmax(0,1fr)_minmax(0,350px)] md:gap-x-8 lg:gap-x-10`
}
